import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { pad: readonly string[]; root: string; chop: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Fm: { pad: ['F3', 'Ab3', 'C4'], root: 'F1', chop: ['F4', 'Ab4', 'C5'] },
  Db: { pad: ['Db3', 'F3', 'Ab3'], root: 'Db2', chop: ['F4', 'Ab4', 'Db5'] },
  Eb: { pad: ['Eb3', 'G3', 'Bb3'], root: 'Eb2', chop: ['G4', 'Bb4', 'Eb5'] },
  Cm: { pad: ['C3', 'Eb3', 'G3'], root: 'C2', chop: ['G4', 'C5', 'Eb5'] },
  Bbm: { pad: ['Bb2', 'Db3', 'F3'], root: 'Bb1', chop: ['F4', 'Bb4', 'Db5'] },
};

const PROGS = [
  ['Fm', 'Fm', 'Db', 'Eb'],
  ['Fm', 'Db', 'Bbm', 'Cm'],
  ['Fm', 'Eb', 'Db', 'Cm'],
  ['Fm', 'Bbm', 'Db', 'Eb'],
] as const;

const KICKS = [
  ['X.....X...X.....', 'X.....X...X..X..'],
  ['X..X......X.....', 'X..X......X...X.'],
  ['X.....X.X.......', 'X.....X...X.X...'],
] as const;

const COW: Note[][] = [
  [[0, 'F5', 1], [3, 'F5', 1], [6, 'Ab5', 1], [8, 'F5', 1], [10, 'C6', 1], [11, 'Bb5', 1], [12, 'Ab5', 1], [14, 'G5', 1]],
  [[0, 'F5', 1], [3, 'F5', 1], [6, 'Ab5', 1], [8, 'F5', 1], [10, 'Db6', 1], [11, 'C6', 1], [12, 'Ab5', 1], [14, 'F5', 1]],
  [[0, 'G5', 1], [3, 'G5', 1], [6, 'Bb5', 1], [8, 'G5', 1], [10, 'Eb6', 1], [11, 'Db6', 1], [12, 'Bb5', 1], [14, 'G5', 1]],
  [[0, 'Eb5', 1], [3, 'Eb5', 1], [6, 'G5', 1], [8, 'Eb5', 1], [10, 'C6', 1], [11, 'Bb5', 1], [12, 'G5', 1], [14, 'Eb5', 1]],
];

type Chop = readonly [step: number, vowel: 'chopA' | 'chopO', len: number, tone: number];

const CHOPS: readonly (readonly Chop[])[] = [
  [[0, 'chopO', 2, 0], [7, 'chopA', 1, 1], [10, 'chopA', 1, 2]],
  [[2, 'chopA', 1, 1], [8, 'chopO', 3, 0], [14, 'chopA', 1, 2]],
  [[0, 'chopA', 1, 2], [3, 'chopA', 1, 1], [12, 'chopO', 2, 0]],
];

const HAT8 = 'x.o.x.o.x.o.x.o.';
const HAT16 = 'xoxoxoxoxoxoxoxo';
const KEYS = [0, 1, -1, 2, -2, 3] as const;
const MINOR = [5, 7, 8, 10, 0, 1, 3] as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'drop', bars: 8, min: 8, rank: 1 },
    { name: 'verse', bars: 8, min: 4, rank: 4 },
    { name: 'break', bars: 4, min: 4, rank: 3, with: 'drop2' },
    { name: 'drop2', bars: 12, min: 8, rank: 2 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(PHONK, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const kicks = ch.pick(KICKS);
  const chops = ch.orig ? CHOPS : [ch.pick(CHOPS), ch.pick(CHOPS), ch.pick(CHOPS)];
  const harmonies = prog.map((c) => [{ at: 0, tones: pcs(c.pad) }]);
  const cowRows = ch.orig ? COW : phrase(ch.rng, COW.map(cellsOf), harmonies, MINOR, [74, 88], 77);
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    const m = midi(tr(n));
    return noteName(m < 29 ? m + 12 : m > 41 ? m - 12 : m);
  };

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 50, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const dropAt = find(form, 'drop')!;
  const drop2At = find(form, 'drop2');
  const brk = find(form, 'break');
  const rng = createRng(`phonk-${seed}`);
  const human = (v: number) => v * (0.93 + rng.float() * 0.14);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const cowPts: [number, number][] = [[g.at(0), 700], [g.at(dropAt) - s, 2600], [g.at(dropAt), 5200]];
  if (brk !== undefined && drop2At !== undefined) cowPts.push([g.at(brk), 5200], [g.at(brk) + s * 4, 800], [g.at(drop2At) - s, 3000], [g.at(drop2At), 6000]);
  const autos: Score['autos'] = [{ v: 'cowLp', points: cowPts }];

  const pattern = (v: string, bar: number, row: string, scale: number) => {
    for (const h of hits(row)) hit(v, bar, h.step, human(h.vel * scale));
  };
  const hats = (bar: number, row: string, roll: boolean, scale: number) => {
    for (const h of hits(row)) if (!roll || h.step < 14) hit('hat', bar, h.step, human(h.vel * scale));
    if (roll) for (const [i, st] of [14, 14 + 2 / 3, 14 + 4 / 3].entries()) hit('hat', bar, st, human((0.45 + i * 0.12) * scale));
  };
  const cow = (bar: number, loop: number, scale: number) => {
    for (const [step, note, len, vel] of cowRows[loop]!) play('cowbell', bar, step, tr(note), len * s * 1.6, human((vel ?? 0.9) * scale));
  };
  const chopBar = (bar: number, row: readonly Chop[], c: Chord, scale: number) => {
    for (const [step, v, len, tone] of row) play(v, bar, step, tr(c.chop[tone]!), len * s, human(0.9 * scale));
  };
  const play808 = (bar: number, row: string, root: string, glideTo?: string) => {
    const steps = hits(row).map((h) => h.step);
    steps.forEach((step, i) => {
      const next = steps[i + 1] ?? 16;
      const ev: Ev = { v: 'b808', t: g.at(bar, step), bar, step, vel: 1, dur: (next - step) * s * 0.92, note: root };
      if (glideTo !== undefined && i === steps.length - 1) ev.glide = { to: glideTo, at: (next - step) * s * 0.45, time: s * 1.4 };
      events.push(ev);
    });
  };

  for (const b of g.bars()) {
    const loop = cycle(b.bar - intro, 4);
    const ab = loop % 2;
    const c = prog[loop]!;
    const root = low(c.root);
    const nextRoot = low(prog[cycle(loop + 1, 4)]!.root);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const second = b.name === 'drop2';
    const verse = b.name === 'verse';
    const fill = b.last && (b.name === 'drop' || verse) && b.bar + 1 < outro;

    if (b.name === 'intro') {
      cow(b.bar, loop, 0.75);
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.6);
      if (b.index >= 2) hats(b.bar, HAT8, b.last, 0.6);
      if (b.last) chopBar(b.bar, chops[0]!, c, 0.8);
    }

    if (drop || verse) {
      pattern('kick', b.bar, kicks[ab]!, verse ? 0.88 : 1);
      play808(b.bar, kicks[ab]!, root, ab === 1 && nextRoot !== root ? nextRoot : undefined);
      if (verse) hit('clap', b.bar, 8, human(1));
      else {
        hit('clap', b.bar, 4, human(0.95));
        hit('clap', b.bar, 12, human(1));
      }
      hats(b.bar, second ? HAT16 : HAT8, ab === 1, second ? 0.85 : 0.8);
      cow(b.bar, loop, verse ? 0.7 : 1);
      if (verse ? ab === 1 : second || e >= 0.7) chopBar(b.bar, chops[second ? loop % chops.length : ab]!, c, verse ? 0.85 : 1);
      if (drop && b.index % 4 === 0) hit('crash', b.bar, 0, b.index === 0 ? 0.9 : 0.6);
      if (verse) chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.55);
      if (second && e >= 0.85 && b.index % 8 === 4) events.push({ v: 'siren', t: g.at(b.bar), end: g.at(b.bar + 2) - s, bar: b.bar, step: 0, vel: 0.8 });
      if (fill) for (const st of [12, 13, 14, 15]) hit('roll', b.bar, st, 0.5 + (st - 12) * 0.15);
    }

    if (b.name === 'break') {
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.7);
      cow(b.bar, loop, 0.8);
      if (b.index === 0) play('b808', b.bar, 0, root, g.barLength * 0.9, 0.9);
      if (b.index === 1) chopBar(b.bar, chops[2]!, c, 0.8);
      if (b.index >= b.bars - 2) hats(b.bar, HAT8, true, 0.55 + 0.2 * (b.index - b.bars + 2));
      if (b.last) for (const st of [8, 10, 12, 13, 14, 15]) hit('roll', b.bar, st, 0.45 + (st - 8) * 0.07);
    }

    if (b.name === 'outro') {
      if (b.index < b.bars - 1) {
        if (b.index < b.bars - 2) {
          pattern('kick', b.bar, kicks[ab]!, 0.9);
          play808(b.bar, kicks[ab]!, root);
          hit('clap', b.bar, 4, human(0.85));
          hit('clap', b.bar, 12, human(0.85));
        }
        hats(b.bar, HAT8, false, 0.6);
        cow(b.bar, loop, 0.7);
        chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.5);
      } else {
        hit('kick', b.bar, 0, 1);
        play('b808', b.bar, 0, low(prog[0]!.root), g.barLength * 0.9, 1);
        play('chopO', b.bar, 0, tr(prog[0]!.chop[0]!), s * 4, 0.9);
        hit('crash', b.bar, 0, 0.7);
      }
    }
  }

  events.push({ v: 'rise', t: g.at(Math.max(0, dropAt - 2)), end: g.at(dropAt) - s * 2, bar: Math.max(0, dropAt - 2), step: 0, vel: 1 });
  if (brk !== undefined && drop2At !== undefined) {
    const from = Math.max(brk, drop2At - 2);
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2At) - s * 2, bar: from, step: 0, vel: 1 });
  }
  for (const bar of [dropAt, drop2At]) if (bar !== undefined) hit('impact', bar, 0, 1);

  return {
    id: trackId(PHONK, seed, p),
    template: PHONK.id,
    seed,
    params: p,
    title: ch.orig ? PHONK.ref.title : titleOf(seed),
    genre: PHONK.genre,
    bpm: p.bpm,
    swing: 50,
    lufs: -9.5,
    key,
    form,
    mix: { reverbDecay: 2.2, delayTime: (60 / p.bpm) * 0.5, delayFeedback: 0.25, drive: 1.8, softness: 1.6, pumpDepth: 0.3, pumpRelease: 0.18 },
    events,
    autos,
    accents: { kick: 'kick', clap: 'snare', cowbell: 'melody', chopA: 'stab', chopO: 'stab', impact: 'drop' },
  };
}

export const PHONK: Template = {
  id: 'phonk',
  genre: 'drift phonk',
  bpm: [130, 160],
  ref: { seed: 'ultima-volta', title: 'Última Volta', params: { bpm: 140, bars: 40, energy: 0.9 } },
  score,
};
